import { z } from "zod";
import type { Operation } from "../types";

const telemetryEventSchema = z.object({
  external_id: z.string(),
  device_id: z.string(),
  actor_id: z.number().nullable(),
  event_type: z.string(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
});
export type TelemetryEventView = z.infer<typeof telemetryEventSchema>;

const listQuery = z.object({
  limit: z.coerce.number().int().min(1).max(500).optional(),
});
type ListQuery = z.infer<typeof listQuery>;

const listResponse = z.object({
  events: z.array(telemetryEventSchema),
});
type ListResponse = z.infer<typeof listResponse>;

const listOp: Operation<void, ListQuery, void, ListResponse> = {
  method: "GET",
  path: () => "/api/telemetry/unsynced",
  query: listQuery,
  response: listResponse,
};

const markSyncedBody = z.object({
  external_ids: z.array(z.string()).min(1).max(500),
});
type MarkSyncedBody = z.infer<typeof markSyncedBody>;

const markSyncedResponse = z.object({ marked: z.number() });
type MarkSyncedResponse = z.infer<typeof markSyncedResponse>;

const markSyncedOp: Operation<void, void, MarkSyncedBody, MarkSyncedResponse> = {
  method: "POST",
  path: () => "/api/telemetry/mark-synced",
  body: markSyncedBody,
  response: markSyncedResponse,
};

// Flush — ship buffered, opt-in, scrubbed usage events to the configured sink.
// A no-op (skipped) unless a real sink is configured AND the user opted in.
const flushBody = z.object({
  max_batches: z.coerce.number().int().min(1).max(50).optional(),
});
type FlushBody = z.infer<typeof flushBody>;

const flushResponse = z.object({
  ok: z.boolean(),
  provider: z.string(),
  flushed: z.number(),
  dropped: z.number(),
  skipped: z.string().optional(),
});
type FlushResponse = z.infer<typeof flushResponse>;

const flushOp: Operation<void, void, FlushBody, FlushResponse> = {
  method: "POST",
  path: () => "/api/telemetry/flush",
  body: flushBody,
  response: flushResponse,
};

// ── Disclosure — the "exactly what we collect" spec, for /transparency ───────
//
// RO-3-12 (#44305) owes this contract: `client/shared/onboarding/telemetry-
// disclosure.tsx` (ported by RO-3-00 / #44398) takes `disclosure` as a PROP, and
// its header records the debt — "There is no mounted Operation that returns a
// `TelemetryDisclosure`, so whoever ports `/transparency` and Settings ▸ Usage
// analytics (RO-3-12 / RO-3-09) owes one. The type is the contract's shape
// already, so it is a thin read endpoint." This is that endpoint.
//
// It is a GENUINE read, and that matters twice over. `describeTelemetry()`
// (`packages/core/src/telemetry/disclosure.ts`) is a pure module — its own header
// says "no DB, no network, no `server-only`", and its ONLY import is
// `./scrubber.js`, which imports nothing at all. So the `READ` sentinel on the
// mount is honest rather than merely method-shaped: following the handler's calls
// out transitively reaches no writer. (That check is the point — `READ` asserts
// only that the METHOD is a read, and a GET whose handler writes passes it
// legitimately.)
//
// The path sits under `/api/telemetry/`, which is ALREADY the 9th entry in
// `PUBLIC_PREFIXES` (`packages/core/src/auth/route-gate.ts`). That is deliberate:
// /transparency is a public-facing surface that rendered unauthenticated on the
// Next stack (it is linked from the pre-onboarding consent screen), and its data
// path has to stay reachable pre-session or the port would silently regress it to
// a 401 on any install with a credential registered. Landing it on the existing
// public prefix keeps that property WITHOUT widening the perimeter by one byte —
// no new PUBLIC_PREFIXES entry, and emphatically no `publicMutation` (that state
// is for a deliberate WRITE on a POST; `classifyMount` throws at boot if it is
// attached to a read).

/** Mirrors `FieldType` in `packages/core/src/telemetry/scrubber.ts`, plus the
 *  `allowNumericProps` bag's synthetic "numeric". */
const disclosedFieldSchema = z.object({
  key: z.string(),
  type: z.enum(["token", "bool", "count", "numeric"]),
  meaning: z.string(),
});

const disclosedEventSchema = z.object({
  name: z.string(),
  title: z.string(),
  category: z.enum(["Onboarding funnel", "Feature adoption"]),
  when: z.string(),
  fields: z.array(disclosedFieldSchema),
});

const neverCollectedSchema = z.object({
  group: z.string(),
  note: z.string(),
  examples: z.array(z.string()),
});

const disclosureResponse = z.object({
  guarantees: z.array(z.string()),
  identity: z.object({ distinctId: z.string(), note: z.string() }),
  events: z.array(disclosedEventSchema),
  neverCollected: z.array(neverCollectedSchema),
  meta: z.object({ eventCount: z.number(), derivedFrom: z.string() }),
});
type DisclosureResponse = z.infer<typeof disclosureResponse>;

const disclosureOp: Operation<void, void, void, DisclosureResponse> = {
  method: "GET",
  path: () => "/api/telemetry/disclosure",
  response: disclosureResponse,
};

export const telemetry = {
  listUnsynced: listOp,
  markSynced: markSyncedOp,
  flush: flushOp,
  disclosure: disclosureOp,
};
